import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { adminGrupoService, adminEmpresaService } from '../../shared/services';
import { 
  Users, 
  Plus, 
  Edit2, 
  Trash2,
  BookOpen,
  ArrowLeft,
  FolderOpen
} from 'lucide-react';

export function AdminGruposPage() {
  const { empresaId } = useParams<{ empresaId: string }>();
  const queryClient = useQueryClient();

  const { data: empresa } = useQuery({
    queryKey: ['admin-empresa', empresaId],
    queryFn: () => adminEmpresaService.getEmpresa(empresaId!),
    enabled: !!empresaId,
  });

  const { data: grupos, isLoading } = useQuery({
    queryKey: ['admin-grupos', empresaId],
    queryFn: () => adminGrupoService.getGrupos(empresaId!),
    enabled: !!empresaId,
  });

  const deleteMutation = useMutation({
    mutationFn: (grupoId: string) => adminGrupoService.deleteGrupo(grupoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-grupos', empresaId] });
    },
  });

  const handleDelete = (grupoId: string, grupoNome: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o grupo "${grupoNome}"?`)) {
      deleteMutation.mutate(grupoId);
    }
  };

  const getCorClasse = (cor?: string) => {
    const cores: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      pink: 'bg-pink-100 text-pink-800 border-pink-200',
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    };
    return cores[cor || 'blue'] || cores.blue;
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/empresas"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Empresas
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FolderOpen className="w-8 h-8 text-blue-600" />
                Grupos de Trilhas
              </h1>
              <p className="text-gray-600 mt-2">
                {empresa?.nome ? `Empresa: ${empresa.nome}` : 'Gerenciar grupos de trilhas'}
              </p>
            </div>
            <Link
              to={`/admin/empresas/${empresaId}/grupos/novo`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Novo Grupo
            </Link>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Grupos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{grupos?.length || 0}</p>
              </div>
              <FolderOpen className="w-10 h-10 text-blue-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Trilhas</p>
                <p className="text-2xl font-bold text-purple-900 mt-1">
                  {grupos?.reduce((sum: number, g: any) => sum + (g.totalTrilhas || 0), 0) || 0}
                </p>
              </div>
              <BookOpen className="w-10 h-10 text-purple-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Usuários</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {grupos?.reduce((sum: number, g: any) => sum + (g.totalUsuarios || 0), 0) || 0}
                </p>
              </div>
              <Users className="w-10 h-10 text-green-600 opacity-50" />
            </div>
          </div>
        </div>

        {/* Lista de Grupos */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando grupos...</p>
            </div>
          ) : grupos && grupos.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 p-6">
              {grupos
                .sort((a: any, b: any) => a.ordem - b.ordem)
                .map((grupo: any) => (
                  <div
                    key={grupo.id}
                    className={`border-2 rounded-lg p-6 hover:shadow-md transition-shadow ${getCorClasse(grupo.cor)}`}
                  >
                    <div className="flex gap-6">
                      {/* Ícone */}
                      <div className={`w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${getCorClasse(grupo.cor)}`}>
                        <FolderOpen className="w-8 h-8" />
                      </div>

                      {/* Conteúdo */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-bold mb-1">
                              {grupo.nome}
                            </h3>
                            {grupo.descricao && (
                              <p className="text-sm opacity-75 mb-2">
                                {grupo.descricao}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/empresas/${empresaId}/grupos/${grupo.id}`}
                              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                              title="Gerenciar Trilhas e Usuários"
                            >
                              <FolderOpen className="w-5 h-5" />
                            </Link>
                            <Link
                              to={`/admin/empresas/${empresaId}/grupos/${grupo.id}/editar`}
                              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit2 className="w-5 h-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(grupo.id, grupo.nome)}
                              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                              title="Excluir"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/50 rounded-full text-xs font-medium">
                            <BookOpen className="w-3 h-3" />
                            {grupo.totalTrilhas || 0} trilhas
                          </span>

                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/50 rounded-full text-xs font-medium">
                            <Users className="w-3 h-3" />
                            {grupo.totalUsuarios || 0} usuários
                          </span>

                          <span className="text-xs opacity-75">
                            Ordem: {grupo.ordem}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Nenhum grupo cadastrado</p>
              <p className="text-sm mb-4">Crie grupos para organizar trilhas por departamento, cargo ou função.</p>
              <Link
                to={`/admin/empresas/${empresaId}/grupos/novo`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Criar Primeiro Grupo
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
